import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    ReactNode,
} from "react";
import "../css/right-sidebar-context.css"
import { CloseIcon } from "../../../../../asset-controller-mod/asset-serv/icons/iconExporter";

type RightSidebarSize = "sm" | "md" | "lg";

type OpenOptions = {
    title?: ReactNode;
    content: ReactNode;
    footer?: ReactNode;
    size?: RightSidebarSize;
    closeOnBackdrop?: boolean;
    widthSize?: string;
};

type RightSidebarContextValue = {
    isOpen: boolean;
    open: (opts: OpenOptions) => void;
    close: () => void;
    setTitle: (title?: ReactNode) => void;
    setContent: (content: ReactNode) => void;
    widthSize?: string
};

const RightSidebarCtx = createContext<RightSidebarContextValue | null>(null);

export const useRightSidebar = () => {
    const ctx = useContext(RightSidebarCtx);
    if (!ctx) throw new Error("useRightSidebar must be used within RightSidebarProvider");
    return ctx;
};

export const RightSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState<ReactNode>(null);
    const [content, setContent] = useState<ReactNode>(null);
    const [closeOnBackdrop, setCloseOnBackdrop] = useState(true);
    const [widthSize, setWidthSize] = useState<string>("430px");

    const close = useCallback(() => setIsOpen(false), []);

    const open = useCallback((opts: OpenOptions) => {
        setTitle(opts.title ?? null);
        setContent(opts.content);
        setCloseOnBackdrop(opts.closeOnBackdrop ?? true);
        setIsOpen(true);
        if (opts.widthSize) setWidthSize(opts.widthSize);
    }, []);

    const value = useMemo<RightSidebarContextValue>(
        () => ({
            isOpen,
            open,
            close,
            setTitle,
            setContent,
            widthSize
        }),
        [isOpen, open, close]
    );

    return (
        <RightSidebarCtx.Provider value={value}>
            {children}
            <div //NOSONAR
                onClick={() => closeOnBackdrop && close()}
                className={`scl--ursb-out-sty-const ${isOpen ? "active" : ""}`}
            />

            <aside //NOSONAR
                role="dialog"
                aria-modal="true"
                className={`scl--ursb-aside-const-sty-box ${isOpen ? "active" : ""}`}
                style={{ width: widthSize }}
            >
                {/* Header */}
                <div className="scl--ursb-head-box-const-styl">
                    <h6>{title}</h6>
                    <button
                        onClick={close}
                        aria-label="Close"
                    >
                        <CloseIcon/>
                    </button>
                </div>
               {content}
            </aside>
        </RightSidebarCtx.Provider>
    );
};